import { useContext, MouseEvent } from 'react';

import { FaqItem } from 'types/models/Faq';
import { EMAIL_FORMAT, LINK_MARKUP_FORMAT } from 'consts/validation';
import AnchorIcon from 'assets/icons/anchor.svg';
import { ConfigContext } from '@providers/ConfigProvider';
import Collapsible from '@components/Collapsible';
import TextContainer from '@components/Collapsible/components/TextContainer';
import Typography from '@components/atoms/Typography';
import Link from '@components/atoms/Link';
import CollapsibleHeader from '@templates/CollapsibleHeader';
import debounce from 'utils/debounce';
import resolveMarkups from 'utils/resolveMarkups';

import classes from './Section.module.scss';

const FAQ_LINK_CLASS = 'faq-link';

type Props = Omit<FaqItem, 'id'> & {
  id: string;
  onItemClick: (id: string) => void;
  activeId: string;
};

const Section = ({ id, title, questions, onItemClick, activeId }: Props): JSX.Element => {
  const { contact } = useContext(ConfigContext);

  return (
    <div className={classes.wrapper}>
      <div className={classes.titleWrapper}>
        <Link href={`#${id}`} id={id} className={classes.tag}>
          <Typography variant="h3" renderAs="h2">
            {title}
          </Typography>
          <AnchorIcon className={classes.anchor} />
        </Link>
      </div>
      <div className={classes.accordionWrapper}>
        {questions.map(({ title: trigger, content, id: questionId }) => {
          const uniqueId = `${id}${questionId}`;

          return (
            <Collapsible
              key={uniqueId}
              isOpened={activeId === uniqueId}
              trigger={(isOpened, onClick) => (
                <CollapsibleHeader isOpened={isOpened} classes={{ wrapper: classes.headerWrapper }} onClick={onClick}>
                  <Typography variant="h4" renderAs="div" weight={isOpened ? '700' : '400'} className={classes.header}>
                    {resolveMarkups(trigger, contact)}
                    <Link id={uniqueId} href={`#${uniqueId}`} className={classes.questionLink}>
                      <AnchorIcon className={classes.image} />
                    </Link>
                  </Typography>
                </CollapsibleHeader>
              )}
              onClick={() => onItemClick(uniqueId)}
              unmountOnClose
              accordion
            >
              <TextContainer className={classes.text}>
                {/* TODO: rewrite to use block editor */}
                <span
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: resolveMarkups(content, contact)
                      .replace(new RegExp(EMAIL_FORMAT, 'g'), `<a href="mailto:$1" class="${classes.link}">$1</a>`)
                      .replace(
                        new RegExp(LINK_MARKUP_FORMAT, 'g'),
                        `<a href="$2" class="${FAQ_LINK_CLASS} ${classes.link}">$1</a>`
                      ),
                  }}
                  onClick={(e: MouseEvent<HTMLElement>) => {
                    const { target } = e;
                    if ((target as HTMLElement).classList.contains(FAQ_LINK_CLASS)) {
                      e.preventDefault();

                      const [, hash] = (target as HTMLAnchorElement).href.split('#');

                      if (hash) {
                        onItemClick(hash);
                        debounce(() => {
                          window.location.hash = hash;
                        }, 300);
                      }
                    }
                  }}
                />
              </TextContainer>
            </Collapsible>
          );
        })}
      </div>
    </div>
  );
};

export default Section;
